import React, { useState, useEffect, useRef } from 'react';
import { Camera, X, Mic, Layers, Video } from 'lucide-react';
import useClassroomStore from '../stores/classroomStore';
import useEducationStore from '../stores/educationStore';
import { startBackgroundBlurLoop } from '../utils/backgroundBlur';
import { useTranslation } from '../i18n';

const BLUR_PX = 12;
const BLUR_FILTER = `blur(${BLUR_PX}px)`;

// Placeholder pre-lesson intro. The real payoff is autoplay rights: playing
// this <video> with sound during the "Увійти на урок" click grants the tab
// media-autoplay rights for the whole session, so teacher-driven videos in
// VirtualClassroom start with sound without any extra tap from the student.
// TODO: swap for a proper intro (Ken flipping a page + page-flip sfx, or
// clouds parting + wind sfx).
const INTRO_VIDEO_URL = '/avatars/videobegining.mp4';
const INTRO_MAX_MS = 6000;

const CameraPreviewModal = () => {
  const { t } = useTranslation();
  const pendingLesson = useClassroomStore(s => s.pendingLesson);
  const blurEnabled = useClassroomStore(s => s.blurEnabled);
  const setBlurEnabled = useClassroomStore(s => s.setBlurEnabled);
  const onCancel = useClassroomStore(s => s.cancelCameraPreview);
  const onEnterClass = useClassroomStore(s => s.enterClass);
  const fetchPresentationWithSlides = useEducationStore(s => s.fetchPresentationWithSlides);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const blurLoopRef = useRef(null);
  const introVideoRef = useRef(null);
  const introTimeoutRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [segmentationReady, setSegmentationReady] = useState(false);
  const [isPlayingIntro, setIsPlayingIntro] = useState(false);

  const getLessonTypeLabel = (lesson) => {
    if (!lesson) return '';
    if (lesson.sourceType === 'club') return t('classroom.cameraPreview.lessonTypeClub');
    switch (lesson.type) {
      case 'trial': return t('classroom.cameraPreview.lessonTypeTrial');
      case 'group': return t('classroom.cameraPreview.lessonTypeGroup');
      case 'speaking': return t('classroom.cameraPreview.lessonTypeSpeaking');
      case 'individual':
      default: return t('classroom.cameraPreview.lessonTypeIndividual');
    }
  };

  useEffect(() => {
    startCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefetch the lesson's presentation while the user is still on the camera
  // preview. By the time they click "Увійти на урок" the JSONB with all slides
  // is already in the store, so the classroom skips the network round-trip and
  // avoids the "Завантаження презентації..." spinner.
  useEffect(() => {
    const presentationId = pendingLesson?.presentationId;
    if (!presentationId) return;
    fetchPresentationWithSlides(presentationId);
  }, [pendingLesson?.presentationId, fetchPresentationWithSlides]);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  useEffect(() => {
    if (!blurEnabled || !stream || !isCameraOn) {
      setSegmentationReady(false);
      if (blurLoopRef.current) {
        try { blurLoopRef.current.stop(); } catch (_) {}
        blurLoopRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return undefined;

    (async () => {
      try {
        const loop = await startBackgroundBlurLoop({
          videoEl: video,
          canvasEl: canvas,
          blurPx: BLUR_PX,
          // Only swap to the canvas layer once the first composite is actually
          // painted. Flipping the flag on setup made Safari show a black
          // rectangle while MediaPipe was still initializing.
          onFirstFrame: () => {
            if (!cancelled) setSegmentationReady(true);
          },
        });
        if (cancelled) {
          try { loop.stop(); } catch (_) {}
          return;
        }
        blurLoopRef.current = loop;
      } catch (e) {
        // Fall back to CSS blur on the video element (already applied by render).
        console.error('Background blur segmentation unavailable:', e);
      }
    })();

    return () => {
      cancelled = true;
      setSegmentationReady(false);
      if (blurLoopRef.current) {
        try { blurLoopRef.current.stop(); } catch (_) {}
        blurLoopRef.current = null;
      }
    };
  }, [blurEnabled, stream, isCameraOn]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: 'user' },
        audio: true
      });
      setStream(mediaStream);
    } catch (err) {
      console.error('Camera access error:', err);
      setCameraError(t('classroom.cameraPreview.cameraError'));
    }
  };

  const toggleCamera = () => {
    if (stream) {
      stream.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsCameraOn(!isCameraOn);
    }
  };

  const toggleMic = () => {
    if (stream) {
      stream.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMicOn(!isMicOn);
    }
  };

  const completeEnter = () => {
    if (introTimeoutRef.current) {
      clearTimeout(introTimeoutRef.current);
      introTimeoutRef.current = null;
    }
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onEnterClass(pendingLesson, { cameraEnabled: isCameraOn, micEnabled: isMicOn });
  };

  const handleEnterClass = () => {
    // Play the intro video synchronously while user activation is fresh — this
    // both shows the intro and grants the tab full media-autoplay rights so the
    // teacher's in-lesson videos can start with sound without a per-video tap.
    const v = introVideoRef.current;
    if (v) {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          // Unmuted autoplay blocked (rare after a click, but iOS can be strict).
          // Fall back to muted playback so the intro at least renders visually.
          try {
            v.muted = true;
            v.play().catch(() => completeEnter());
          } catch (_) {
            completeEnter();
          }
        });
      }
    }
    setIsPlayingIntro(true);
    // Safety net: if the video errors, stalls, or the onEnded event is lost,
    // hand off to the classroom after a bounded wait instead of hanging.
    introTimeoutRef.current = setTimeout(completeEnter, INTRO_MAX_MS);
  };

  const handleCancel = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onCancel();
  };

  return (
    <>
      {/* Intro overlay. The <video> is always mounted so introVideoRef stays
          valid — handleEnterClass calls .play() synchronously during the user
          click, then flips this container to fullscreen. */}
      <div
        className={
          isPlayingIntro
            ? 'fixed inset-0 z-[110] bg-white flex items-center justify-center'
            : 'hidden'
        }
      >
        <video
          ref={introVideoRef}
          src={INTRO_VIDEO_URL}
          className="w-full h-full object-contain"
          playsInline
          preload="auto"
          onEnded={completeEnter}
          onError={completeEnter}
        />
      </div>
    <div className={`fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-2 sm:p-4 pb-[env(safe-area-inset-bottom)] ${isPlayingIntro ? 'hidden' : ''}`}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[100dvh] sm:max-h-[95dvh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-brand-primary text-white p-4 flex justify-between items-center flex-shrink-0">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Camera size={24} />
            {t('classroom.cameraPreview.title')}
          </h2>
          <button
            onClick={handleCancel}
            className="hover:bg-white/20 rounded-full p-1 transition"
          >
            <X size={24} />
          </button>
        </div>

        {/* Video Preview */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
          <div className="relative bg-gray-900 rounded-xl overflow-hidden aspect-video mb-4">
            {cameraError ? (
              <div className="absolute inset-0 flex items-center justify-center text-white text-center p-4">
                <div>
                  <Camera size={48} className="mx-auto mb-4 opacity-50" />
                  <p>{cameraError}</p>
                  <button
                    onClick={startCamera}
                    className="mt-4 bg-brand-primary hover:bg-brand-primary/80 px-4 py-2 rounded-lg transition"
                  >
                    {t('classroom.cameraPreview.tryAgain')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className={`w-full h-full object-cover ${!isCameraOn ? 'hidden' : ''}`}
                  style={{
                    transform: 'scaleX(-1)',
                    // Video stays visible so it never stops decoding frames
                    // (Safari has been observed to starve <video> pipelines
                    // when hidden with visibility:hidden). Until the canvas
                    // overlay has its first real composite ready, we apply a
                    // whole-frame CSS blur so nothing sharp leaks through.
                    filter: blurEnabled && !segmentationReady ? BLUR_FILTER : 'none'
                  }}
                />
                {blurEnabled && isCameraOn && (
                  <canvas
                    ref={canvasRef}
                    className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-150 ${segmentationReady ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transform: 'scaleX(-1)' }}
                  />
                )}
                {!isCameraOn && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-white text-center">
                      <Camera size={48} className="mx-auto mb-2 opacity-50" />
                      <p>{t('classroom.cameraPreview.cameraOff')}</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
            {/* Camera toggle */}
            <button
              onClick={toggleCamera}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl transition font-medium ${
                isCameraOn
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  : 'bg-red-100 text-red-600 hover:bg-red-200'
              }`}
            >
              <Camera size={20} />
              {isCameraOn ? t('classroom.cameraPreview.turnCameraOff') : t('classroom.cameraPreview.turnCameraOn')}
            </button>

            {/* Mic toggle */}
            <button
              onClick={toggleMic}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl transition font-medium ${
                isMicOn
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  : 'bg-red-100 text-red-600 hover:bg-red-200'
              }`}
            >
              <Mic size={20} />
              {isMicOn ? t('classroom.cameraPreview.turnMicOff') : t('classroom.cameraPreview.turnMicOn')}
            </button>

            {/* Blur toggle */}
            <button
              onClick={() => setBlurEnabled(!blurEnabled)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl transition font-medium ${
                blurEnabled
                  ? 'bg-brand-primary text-white hover:bg-brand-primary/80'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              <Layers size={20} />
              {blurEnabled ? t('classroom.cameraPreview.blurOn') : t('classroom.cameraPreview.blurOff')}
            </button>
          </div>

          {/* Blur info */}
          {blurEnabled && (
            <div className="bg-brand-sage/20 rounded-lg p-3 mb-4 text-sm text-gray-600">
              <p className="flex items-center gap-2">
                <Layers size={16} className="text-brand-primary" />
                {t('classroom.cameraPreview.blurNotice')}
              </p>
            </div>
          )}

          {/* Lesson info */}
          {pendingLesson && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-500 mb-1">{t('classroom.cameraPreview.lessonLabel')}</p>
              <p className="font-semibold text-gray-900">
                {getLessonTypeLabel(pendingLesson)}
              </p>
              <p className="text-sm text-gray-600">
                {pendingLesson.teacherName || pendingLesson.studentName}
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition"
            >
              {t('classroom.cameraPreview.cancel')}
            </button>
            <button
              onClick={handleEnterClass}
              className="flex-1 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
            >
              <Video size={20} />
              {t('classroom.cameraPreview.enterLesson')}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default CameraPreviewModal;
